import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { API, getAuthToken } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useTableSort, SortableHeader } from "@/lib/tableSort";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Upload, Download, FileText, ExternalLink, Search, X, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";

// Helper to get proper document URL
const getDocumentUrl = (url) => {
  if (!url) return null;
  // If it's already a full URL, return as is
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  // Otherwise, it's a storage path - use our API to download
  return `${API}/files/${url}?auth=${getAuthToken()}`;
};

export default function Sellers() {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSeller, setEditingSeller] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadingBulk, setUploadingBulk] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const fileInputRef = useRef(null);
  const bulkFileInputRef = useRef(null);
  
  // Search and Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [gstinFilter, setGstinFilter] = useState("all");
  
  const [formData, setFormData] = useState({
    seller_name: "",
    seller_code: "",
    billing_address: "",
    shipping_address: "",
    city: "",
    state: "",
    pincode: "",
    contact_number: "",
    email: "",
    gstin: "",
    seller_type: "",
    gstin_document_url: null
  });

  useEffect(() => {
    fetchSellers();
  }, []);

  // Get unique states and cities for filter dropdowns
  const uniqueStates = useMemo(() => {
    const states = [...new Set(sellers.map(s => s.state).filter(Boolean))];
    return states.sort();
  }, [sellers]);

  const uniqueCities = useMemo(() => {
    const cities = [...new Set(sellers.map(s => s.city).filter(Boolean))];
    return cities.sort();
  }, [sellers]);

  // Filtered sellers based on search and filters
  const filteredSellers = useMemo(() => {
    return sellers.filter(seller => {
      // Search filter (Seller Name, GSTIN, Email)
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery ||
        seller.seller_name.toLowerCase().includes(searchLower) ||
        (seller.seller_code && seller.seller_code.toLowerCase().includes(searchLower));
      
      // State filter
      const matchesState = stateFilter === "all" || seller.state === stateFilter;
      
      // City filter
      const matchesCity = cityFilter === "all" || seller.city === cityFilter;
      
      // Has GSTIN filter
      const matchesGstin = gstinFilter === "all" || 
        (gstinFilter === "yes" && seller.gstin && seller.gstin.trim() !== "") ||
        (gstinFilter === "no" && (!seller.gstin || seller.gstin.trim() === ""));
      
      return matchesSearch && matchesState && matchesCity && matchesGstin;
    });
  }, [sellers, searchQuery, stateFilter, cityFilter, gstinFilter]);

  // Sort: default Seller Name A–Z.
  const { sortedRows: sortedSellers, sort, toggleSort } = useTableSort(filteredSellers, {
    defaultKey: "seller_name",
    defaultOrder: "asc",
    accessors: {
      seller_code: (s) => s.seller_code,
      seller_name: (s) => s.seller_name,
      city: (s) => s.city,
      state: (s) => s.state,
      created_at: (s) => s.created_at,
    },
  });

  const clearFilters = () => {
    setSearchQuery("");
    setStateFilter("all");
    setCityFilter("all");
    setGstinFilter("all");
  };

  const hasActiveFilters = searchQuery || stateFilter !== "all" || cityFilter !== "all" || gstinFilter !== "all";

  const fetchSellers = async () => {
    try {
      const response = await axios.get(`${API}/sellers`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      setSellers(response.data);
      setSelectedIds([]); // Clear selections after refresh
    } catch (error) {
      toast.error("Error fetching sellers");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredSellers.map(s => s.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id, checked) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(i => i !== id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    try {
      await Promise.all(
        selectedIds.map(id => 
          axios.delete(`${API}/sellers/${id}`, {
            headers: { Authorization: `Bearer ${getAuthToken()}` }
          })
        )
      );
      toast.success(`${selectedIds.length} seller(s) deleted successfully`);
      fetchSellers();
    } catch (error) {
      toast.error("Error deleting sellers");
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingDoc(true);
    try {
      const formDataFile = new FormData();
      formDataFile.append("file", file);

      const response = await axios.post(`${API}/upload/gstin`, formDataFile, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`
        }
      });

      setFormData({ 
        ...formData, 
        gstin_document_url: response.data.url,
        // Auto-fill GSTIN if extracted from document
        gstin: response.data.extracted_gstin || formData.gstin
      });
      
      if (response.data.extracted_gstin) {
        toast.success(`Document uploaded. GSTIN extracted: ${response.data.extracted_gstin}`);
      } else {
        toast.success("Document uploaded successfully");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error uploading document");
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingBulk(true);
    setUploadResult(null);
    try {
      const formDataFile = new FormData();
      formDataFile.append("file", file);

      const response = await axios.post(`${API}/sellers/upload`, formDataFile, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`
        },
        timeout: 120000
      });

      const result = response.data;
      setUploadResult(result);
      
      if (result.failed === 0) {
        toast.success(`Successfully uploaded ${result.success} sellers`);
      } else {
        toast.warning(`Uploaded ${result.success} sellers. ${result.failed} failed.`);
      }
      fetchSellers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error uploading file");
    } finally {
      setUploadingBulk(false);
      if (bulkFileInputRef.current) {
        bulkFileInputRef.current.value = "";
      }
    }
  };

  const downloadTemplate = () => {
    const csvContent = "seller_name,seller_code,billing_address,shipping_address,city,state,pincode,contact_number,email,seller_type,gstin,gstin_document_url\n" +
                       "Example Seller,SEL001,123 Main St,456 Oak Ave,Mumbai,Maharashtra,400001,9876543210,seller@example.com,B2B,27AABCU9603R1ZM,https://example.com/doc.pdf";
    
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sellers_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleExport = () => {
    const rows = sortedSellers.map((s) => ({
      "Seller Code": s.seller_code || "",
      "Seller Name": s.seller_name || "",
      "Seller Type": s.seller_type || "",
      "Contact Number": s.contact_number || "",
      "Email ID": s.email || "",
      "GSTIN": s.gstin || "",
      "Billing Address": s.billing_address || "",
      "Shipping Address": s.shipping_address || "",
      "City": s.city || "",
      "State": s.state || "",
      "Pincode": s.pincode || "",
      "Created At": s.created_at ? new Date(s.created_at).toLocaleString() : "",
    }));

    if (rows.length === 0) {
      toast.error("No sellers to export for the current filters");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sellers");
    const dateStr = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `sellers_export_${dateStr}.xlsx`);
    toast.success(`Exported ${rows.length} seller${rows.length === 1 ? "" : "s"}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSeller) {
        await axios.put(`${API}/sellers/${editingSeller.id}`, formData, {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        });
        toast.success("Seller updated successfully");
      } else {
        await axios.post(`${API}/sellers`, formData, {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        });
        toast.success("Seller created successfully");
      }
      setDialogOpen(false);
      setEditingSeller(null);
      resetForm();
      fetchSellers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "An error occurred");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this seller?")) return;
    try {
      await axios.delete(`${API}/sellers/${id}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      toast.success("Seller deleted successfully");
      fetchSellers();
    } catch (error) {
      toast.error("Error deleting seller");
    }
  };

  const handleEdit = (seller) => {
    setEditingSeller(seller);
    setFormData({
      seller_name: seller.seller_name,
      seller_code: seller.seller_code,
      billing_address: seller.billing_address,
      shipping_address: seller.shipping_address,
      city: seller.city,
      state: seller.state,
      pincode: seller.pincode || "",
      contact_number: seller.contact_number,
      email: seller.email || "",
      gstin: seller.gstin || "",
      seller_type: seller.seller_type,
      gstin_document_url: seller.gstin_document_url
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      seller_name: "",
      seller_code: "",
      billing_address: "",
      shipping_address: "",
      city: "",
      state: "",
      pincode: "",
      contact_number: "",
      email: "",
      gstin: "",
      seller_type: "",
      gstin_document_url: null
    });
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingSeller(null);
    resetForm();
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[#0F0F0F]" data-testid="sellers-title">Sellers</h1>
          <p className="text-muted-foreground mt-1">Manage your distributors and wholesalers</p>
        </div>
        <div className="flex gap-2">
          <input
            type="file"
            ref={bulkFileInputRef}
            onChange={handleBulkUpload}
            accept=".csv,.xlsx,.xls"
            className="hidden"
          />
          <Button 
            onClick={() => bulkFileInputRef.current?.click()}
            variant="outline"
            disabled={uploadingBulk}
            data-testid="upload-sellers-button"
          >
            {uploadingBulk ? (
              <>
                <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Processing...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload Sellers
              </>
            )}
          </Button>
          <Button 
            onClick={downloadTemplate}
            variant="outline"
            data-testid="download-template-button"
          >
            <Download className="h-4 w-4 mr-2" />
            Download Template
          </Button>
          <Button 
            onClick={handleExport}
            variant="outline"
            data-testid="export-sellers-button"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-accent hover:bg-accent/90 text-white" 
                data-testid="add-seller-button"
                onClick={() => {
                  setEditingSeller(null);
                  resetForm();
                  setDialogOpen(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Seller
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle data-testid="seller-dialog-title">{editingSeller ? "Edit Seller" : "Add New Seller"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="seller_name">Seller Name *</Label>
                    <Input
                      id="seller_name"
                      value={formData.seller_name}
                      onChange={(e) => setFormData({ ...formData, seller_name: e.target.value })}
                      required
                      data-testid="seller-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="seller_code">Seller Code *</Label>
                    <Input
                      id="seller_code"
                      value={formData.seller_code}
                      onChange={(e) => setFormData({ ...formData, seller_code: e.target.value })}
                      required
                      data-testid="seller-code-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="billing_address">Billing Address *</Label>
                  <Input
                    id="billing_address"
                    value={formData.billing_address}
                    onChange={(e) => setFormData({ ...formData, billing_address: e.target.value })}
                    required
                    data-testid="billing-address-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shipping_address">Shipping Address *</Label>
                  <Input
                    id="shipping_address"
                    value={formData.shipping_address}
                    onChange={(e) => setFormData({ ...formData, shipping_address: e.target.value })}
                    required
                    data-testid="shipping-address-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      required
                      data-testid="city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      required
                      data-testid="state-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pincode">Pincode *</Label>
                    <Input
                      id="pincode"
                      value={formData.pincode}
                      onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                      required
                      data-testid="pincode-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_number">Contact Number *</Label>
                    <Input
                      id="contact_number"
                      value={formData.contact_number}
                      onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                      required
                      data-testid="contact-number-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email ID *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="seller@example.com"
                    data-testid="email-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="gstin">GSTIN (Optional)</Label>
                    <Input
                      id="gstin"
                      value={formData.gstin}
                      onChange={(e) => setFormData({ ...formData, gstin: e.target.value })}
                      placeholder="Auto-extracted from document"
                      data-testid="gstin-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="seller_type">Seller Type *</Label>
                    <Select
                      value={formData.seller_type}
                      onValueChange={(value) => setFormData({ ...formData, seller_type: value })}
                      required
                    >
                      <SelectTrigger data-testid="seller-type-select">
                        <SelectValue placeholder="Select seller type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="B2B">B2B</SelectItem>
                        <SelectItem value="B2C">B2C</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gstin_document">GSTIN / Aadhaar / PAN Document *</Label>
                  <div className="flex gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleDocumentUpload}
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      variant="outline"
                      disabled={uploadingDoc}
                      className="w-full"
                      data-testid="upload-document-button"
                    >
                      {uploadingDoc ? "Uploading..." : formData.gstin_document_url ? "Change Document" : "Upload Document *"}
                    </Button>
                  </div>
                  {formData.gstin_document_url && (
                    <p className="text-xs text-success">✓ Document uploaded</p>
                  )}
                  {!formData.gstin_document_url && (
                    <p className="text-xs text-muted-foreground">Upload GSTIN/Aadhaar/PAN document (required)</p>
                  )}
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-accent hover:bg-accent/90 text-white" 
                  data-testid="submit-seller-button"
                  disabled={!formData.gstin_document_url}
                >
                  {editingSeller ? "Update Seller" : "Create Seller"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>All Sellers ({filteredSellers.length})</CardTitle>
          {selectedIds.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" data-testid="bulk-delete-button">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Selected ({selectedIds.length})
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Selected Sellers?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete {selectedIds.length} selected seller(s)? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleBulkDelete} className="bg-destructive hover:bg-destructive/90">
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </CardHeader>
        <CardContent>
          {/* Sticky Search and Filters */}
          <div className="sticky top-0 z-10 bg-white pb-4 mb-4 border-b">
            <div className="flex flex-wrap gap-3">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by Seller Name or Seller Code..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="sellers-search-input"
                />
              </div>
              
              {/* State Filter */}
              <Select value={stateFilter} onValueChange={setStateFilter}>
                <SelectTrigger className="w-[150px]" data-testid="state-filter">
                  <SelectValue placeholder="State" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  {uniqueStates.map(state => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* City Filter */}
              <Select value={cityFilter} onValueChange={setCityFilter}>
                <SelectTrigger className="w-[150px]" data-testid="city-filter">
                  <SelectValue placeholder="City" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {uniqueCities.map(city => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Has GSTIN Filter */}
              <Select value={gstinFilter} onValueChange={setGstinFilter}>
                <SelectTrigger className="w-[150px]" data-testid="gstin-filter">
                  <SelectValue placeholder="Has GSTIN" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Has GSTIN</SelectItem>
                  <SelectItem value="no">No GSTIN</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-button">
                  <X className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-auto max-h-[600px]">
              <Table containerClassName="overflow-visible">
                <TableHeader className="sticky top-0 z-20">
                  <TableRow className="bg-slate-100 hover:bg-slate-100">
                    <TableHead className="w-12 bg-slate-100 border-b-2 border-slate-200">
                      <Checkbox
                        checked={filteredSellers.length > 0 && selectedIds.length === filteredSellers.length}
                        onCheckedChange={handleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    <SortableHeader sortKey="seller_code" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">Seller Code</SortableHeader>
                    <SortableHeader sortKey="seller_name" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">Seller Name</SortableHeader>
                    <SortableHeader sortKey="city" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">City</SortableHeader>
                    <SortableHeader sortKey="state" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">State</SortableHeader>
                    <TableHead className="bg-slate-100 border-b-2 border-slate-200 font-semibold text-slate-700">Contact</TableHead>
                    <TableHead className="bg-slate-100 border-b-2 border-slate-200 font-semibold text-slate-700">Type</TableHead>
                    <TableHead className="bg-slate-100 border-b-2 border-slate-200 font-semibold text-slate-700">Document</TableHead>
                    <TableHead className="text-right bg-slate-100 border-b-2 border-slate-200 font-semibold text-slate-700">Actions</TableHead>
                  </TableRow>
                </TableHeader>
              <TableBody>
                {filteredSellers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      {sellers.length === 0 
                        ? "No sellers found. Add your first seller to get started."
                        : "No sellers match your search/filter criteria."}
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedSellers.map((seller) => (
                    <TableRow key={seller.id} data-testid={`seller-row-${seller.id}`}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.includes(seller.id)}
                          onCheckedChange={(checked) => handleSelectOne(seller.id, checked)}
                          data-testid={`select-seller-${seller.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm">{seller.seller_code}</TableCell>
                      <TableCell className="font-medium">{seller.seller_name}</TableCell>
                      <TableCell>{seller.city}</TableCell>
                      <TableCell>{seller.state}</TableCell>
                      <TableCell>{seller.contact_number}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          seller.seller_type === "B2B" ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"
                        }`}>
                          {seller.seller_type}
                        </span>
                      </TableCell>
                      <TableCell>
                        {seller.gstin_document_url ? (
                          <a 
                            href={getDocumentUrl(seller.gstin_document_url)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                            data-testid={`download-doc-${seller.id}`}
                          >
                            <FileText className="h-4 w-4" />
                            <Download className="h-3 w-3" />
                          </a>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(seller)}
                            data-testid={`edit-seller-${seller.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDelete(seller.id)}
                            data-testid={`delete-seller-${seller.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Upload Result Dialog */}
      <AlertDialog open={!!uploadResult} onOpenChange={(open) => { if (!open) setUploadResult(null); }}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="upload-result-title">Upload Complete</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <div className="flex gap-4">
                  <div className="flex-1 p-3 bg-green-50 border border-green-200 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-700" data-testid="upload-success-count">{uploadResult?.success || 0}</p>
                    <p className="text-xs text-green-600">Successful</p>
                  </div>
                  <div className="flex-1 p-3 bg-red-50 border border-red-200 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-700" data-testid="upload-failed-count">{uploadResult?.failed || 0}</p>
                    <p className="text-xs text-red-600">Failed</p>
                  </div>
                </div>
                {uploadResult?.errors?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm font-medium text-slate-700 mb-1">Error Details:</p>
                    <div className="max-h-48 overflow-y-auto border rounded p-2 bg-slate-50 text-sm">
                      {uploadResult.errors.map((err, i) => (
                        <div key={i} className="py-1 border-b last:border-0 text-red-700">
                          <span className="font-mono text-xs">Row {err.row}:</span> {err.error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setUploadResult(null)}>Close</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
